import "../ui/styles/pets.css";
import "../ui/styles/petHub.css";
import { Pets, petSheet } from "@highmarch/app";
import { audio } from "../audio/audio";
import { petLink } from "../ui/pets/petLink";
import { petChipHtml, petRowHtml } from "../ui/pets/petChip";
import { bindPetRowTaps, shownPet } from "../ui/pets/petRowTaps";
import { catchBlockHtml } from "../ui/pets/catchBlock";
import { treatWellHtml } from "../ui/pets/treatWell";
import { HubPetTaps } from "../ui/pets/hubPetTap";
import type { EndScreenFacts, Feature, HubApi, PreBattleContext } from "./registry";

/** The pet chip sits just above the supplies row (features/supplies.ts ROW_ORDER 10). */
const ROW_ORDER = 9;
/** The end screen's catch row: after the event's embers (20). */
const END_ORDER = 25;
/** The catch's sound comes in with its pop (pets.css). */
const CATCH_CUE_MS = 450;
/** Before any catch, the empty chip shows on the battle card from this many levels before wild pets roam. */

/**
 * Pets (docs/PETS.md; Pets v2: caught in battle, levelled like towers): the battle card's and the map panel's pet row
 * (the chip — a tap opens the picker of every pet had, or none — and the Pet Treat well with its picker; before any catch,
 * the empty chip saying where wild pets roam), the pet and the wild encounter in every new battle's modifiers (chained
 * after the supplies' `prepareRun`), the catch settled (petLink.settle, called by the battle the moment it lands; the
 * end screen settles again, idempotently, and shows its row), every hub pet's tap target (→ its sheet), a Guide
 * topic, and the link the cosmetics and the Skins picker read (ui/pets/petLink.ts). The rules are @highmarch/app `Pets`;
 * the `pets` save section is written with the catalog (LevelCatalog.applyTo).
 */
export const petsFeature: Feature = {
  id: "pets",
  register(ctx, registry) {
    const pets = new Pets(ctx.catalog);
    const W = ctx.balance.pets.wild;
    let worn = pets.equipped()?.id ?? null;
    let hub: HubApi | null = null;
    /** A pet newly caught or equipped: the renderer wears the pet again (the cosmetics' WornSet). */
    const sync = (force = false): void => {
      const now = pets.equipped()?.id ?? null;
      if (force || now !== worn) { worn = now; petLink.changed(); }
    };
    const settle = (sim: EndScreenFacts["sim"]) => {
      const s = pets.settle(sim, ctx.now());
      if (s.treatUsed !== null || s.grant?.fresh) { ctx.persist(); sync(true); }
      return s.grant;
    };
    petLink.set({
      owned: () => pets.owned().map((p) => ({ id: p.id, name: p.name, level: pets.levelOf(p.id) })),
      roster: () => ctx.balance.pets.list.map((p) => ({ id: p.id, name: p.name, level: pets.levelOf(p.id) })),
      equipped: () => pets.equipped()?.id ?? null,
      settle,
    });

    registry.preBattleRow({ id: "pets", order: ROW_ORDER, html: (c) => rowHtml(pets, c, W.fromLevel, W.skinChanceBp / 100) });

    // The supplies' preparer runs first (features/index.ts lists it before this one), then the pet joins the run.
    const prev = registry.prepareRun;
    registry.prepareRun = (level, modifiers) => pets.prepareRun(level, prev(level, modifiers));

    // The end screen's row for what the wild pet paid (settled already in battle; idempotent per run).
    const cued = new Set<string>();
    registry.endScreenExtra({
      id: "pets", order: END_ORDER,
      html: (f) => {
        const grant = settle(f.sim);
        const pet = grant ? ctx.balance.pets.list.find((p) => p.id === grant.reward.petId) : undefined;
        return grant && pet ? catchBlockHtml(grant, pet) : "";
      },
      bind: (_block, f) => {
        const key = `${f.level}:${f.sim.seed}`;
        if (f.sim.wildPet?.phase !== "caught" || cued.has(key)) return;
        cued.add(key);
        window.setTimeout(() => audio.play("towerUpgrade"), CATCH_CUE_MS);
      },
    });

    // The hub: every pet on the lawn is a tap target for its sheet (Pets v3).
    const sheetOf = (id: string) => { const p = pets.owned().find((x) => x.id === id); return p ? petSheet(ctx.balance, ctx.catalog.profile, p, pets.equipped()?.id ?? null) : null; };
    registry.hubWidget({
      id: "pets",
      mount: (h, layer) => {
        hub = h;
        const taps = new HubPetTaps(layer, () => pets.owned().map((p) => p.id), (id) => h.anchor({ pet: id }), sheetOf, (id) => h.openPet?.(id));
        return { update: () => taps.update(), dispose: () => { taps.dispose(); if (hub === h) hub = null; } };
      },
    });

    bindPetRowTaps({ root: ctx.ui, pets, registry, hub: () => hub, base: W.skinChanceBp / 100, persist: ctx.persist, changed: () => sync() });

    registry.guideTopic({
      id: "pets", group: "castle", label: "Pets", art: { icon: "heart" }, fromLevel: W.fromLevel, steps: [],
      lines: [
        "A wild pet may scamper onto the grass! Tap it and it comes down the road: wear it down to catch it.",
        "Six roam the realm, each from its own level. Pick one on the battle card to fight at our flag.",
        "Catch one again and it gains a level. Upgrade it on the **Towers** tab: it never passes your best tower's level.",
        "Now and then one wears a rare coat: catch it to keep the skin. Arm a **Pet Treat** for better odds.",
      ],
    });

    if (ctx.debug && typeof window !== "undefined") {
      (window as unknown as { __tdPets: unknown }).__tdPets = {
        pets,
        /** Captures: own a pet at `level` (caught, then raised). */
        level: (id: string, level: number) => { pets.state.unlock(id); const i = ctx.balance.pets.list.findIndex((p) => p.id === id); pets.state.raiseLevelAt(i, level); ctx.persist(); sync(true); hub?.changed(); },
        /** Captures: hold `n` of each treat (or of one). */
        treats: (n = 2, id?: string) => { for (const t of ctx.balance.pets.treats) if (!id || id === t.id) pets.state.addTreat(t.id, n); ctx.persist(); hub?.changed(); },
      };
    }
  },
};

/** The row: the chip (or, before any catch and near the levels wild pets roam, the empty one) and the treat well. */
function rowHtml(pets: Pets, c: PreBattleContext, fromLevel: number, base: number): string {
  const s = shownPet(pets);
  const treat = c.level >= fromLevel ? treatWellHtml(pets.treats(), base) : "";
  if (s) return petRowHtml(c.surface, petChipHtml(pets.card(s.pet), s.on), treat);
  // no empty "Wild pets" slot before the first catch (owner 2026-10-23: nothing that doesn't apply); the treat well
  // stands alone where wild pets roam
  return treat ? petRowHtml(c.surface, "", treat) : "";
}
